import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode, type TransitionEvent as ReactTransitionEvent } from "react";
import Avatar from "@mui/material/Avatar";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { ArrowLeft, ArrowRight, BellPlus, BellRing, CircleCheck, ExternalLink, Eye, FolderInput, Heart, Images, LoaderCircle, MessagesSquare, Pin, RotateCcw, Store, X, ZoomIn, ZoomOut } from "lucide-react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import type { Post } from "../../shared/contracts";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { getMallProductHref } from "../lib/external-links";
import { formatRelativeTime, initials } from "../lib/format";
import { getGallerySlides, resolveGalleryGesture } from "../lib/gallery-gesture";
import { toast } from "../lib/toast";
import { Button } from "./ui/button";
import { ExpandableText } from "./ExpandableText";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";

interface TouchPoint { x: number; y: number }
interface GallerySwipeStart extends TouchPoint { startedOnImage: boolean }
interface PanPoint { x: number; y: number }
type GalleryTransitionAction = "previous" | "next" | "reset";

export function PostCard({ initialPost, compact = false, detail = false, isolated = false, showFollow = false, ownerActions, contentAction }: { initialPost: Post; compact?: boolean; detail?: boolean; isolated?: boolean; showFollow?: boolean; ownerActions?: ReactNode; contentAction?: ReactNode }) {
  const [post, setPost] = useState(initialPost);
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferComplete, setTransferComplete] = useState(false);
  const [bursting, setBursting] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [pan, setPan] = useState<PanPoint>({ x: 0, y: 0 });
  const pointersRef = useRef(new Map<number, TouchPoint>());
  const pinchRef = useRef<{ distance: number; scale: number } | null>(null);
  const swipeRef = useRef<GallerySwipeStart | null>(null);
  const multiTouchRef = useRef(false);
  const dragRef = useRef<{ pointerId: number; start: TouchPoint; origin: PanPoint } | null>(null);
  const galleryOffsetRef = useRef(0);
  const galleryTransitionRef = useRef<GalleryTransitionAction | null>(null);
  const galleryTransitionTimerRef = useRef<number | null>(null);
  const preloadedGalleryImagesRef = useRef<HTMLImageElement[]>([]);
  const panRef = useRef<PanPoint>({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const stageRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const { config, user, requestLogin } = useApp();
  const navigate = useNavigate();
  const linksToDetail = !detail && !isolated;
  const galleryOpen = galleryIndex != null;
  const activeGalleryIndex = galleryIndex == null || !post.media.length ? null : ((galleryIndex % post.media.length) + post.media.length) % post.media.length;
  const gallerySlides = galleryIndex == null ? [] : getGallerySlides(galleryIndex, post.media.length);
  const productHref = post.listingState !== "sold" && post.productUrl
    ? getMallProductHref(post.productUrl)
    : null;
  const contentLabel = post.gameName || "动态";
  const hasTags = Boolean(post.gameName || post.serverName || post.category);
  const canTransfer = Boolean(user?.role === "seller" && user.postTransferEnabled && user.id !== post.author.id
    && post.author.role === "seller" && post.status === "published" && post.visibility === "public"
    && post.listingState !== "stored" && !post.transferSource);

  useEffect(() => setPost(initialPost), [initialPost]);

  useEffect(() => {
    const handleFollowUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ authorId: string; following: boolean }>).detail;
      if (detail.authorId !== post.author.id) return;
      setPost((current) => ({ ...current, author: { ...current.author, isFollowing: detail.following } }));
    };
    window.addEventListener("share-moments:follow-updated", handleFollowUpdated);
    return () => window.removeEventListener("share-moments:follow-updated", handleFollowUpdated);
  }, [post.author.id]);

  useEffect(() => {
    if (!galleryOpen) return;
    document.documentElement.classList.add("gallery-open");
    return () => {
      document.documentElement.classList.remove("gallery-open");
      if (galleryTransitionTimerRef.current != null) window.clearTimeout(galleryTransitionTimerRef.current);
    };
  }, [galleryOpen]);

  useEffect(() => {
    if (!galleryOpen) {
      preloadedGalleryImagesRef.current = [];
      return;
    }
    const preloaded = post.media.map((media) => {
      const image = new Image();
      image.decoding = "async";
      image.src = media.url;
      if (typeof image.decode === "function") void image.decode().catch(() => undefined);
      return image;
    });
    preloadedGalleryImagesRef.current = preloaded;
    return () => {
      if (preloadedGalleryImagesRef.current === preloaded) preloadedGalleryImagesRef.current = [];
    };
  }, [galleryOpen, post.media]);

  const updatePan = (value: PanPoint) => {
    panRef.current = value;
    setPan(value);
  };

  const clampPan = (value: PanPoint, scale = zoomRef.current): PanPoint => {
    const stage = stageRef.current;
    const image = imageRef.current;
    if (!stage || !image || scale <= 1) return { x: 0, y: 0 };
    const baseWidth = image.offsetWidth;
    const baseHeight = image.offsetHeight;
    const maxX = Math.max(0, (baseWidth * scale - stage.clientWidth) / 2);
    const maxY = Math.max(0, (baseHeight * scale - stage.clientHeight) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, value.x)), y: Math.min(maxY, Math.max(-maxY, value.y)) };
  };

  const updateZoom = (value: number) => {
    const next = Math.min(Math.max(value, 1), 4);
    zoomRef.current = next;
    setZoomScale(next);
    updatePan(next === 1 ? { x: 0, y: 0 } : clampPan(panRef.current, next));
  };

  useEffect(() => {
    if (!bursting) return;
    const timer = window.setTimeout(() => setBursting(false), 650);
    return () => window.clearTimeout(timer);
  }, [bursting]);

  const toggleLike = async () => {
    if (!user) {
      requestLogin("请先登录账号，才可以点赞动态。", window.location.pathname);
      return;
    }
    setBusy(true);
    try {
      const result = await api<{ liked: boolean; likeCount: number }>(`/posts/${post.id}/like`, { method: post.likedByMe ? "DELETE" : "PUT" });
      setPost((current) => ({ ...current, likedByMe: result.liked, likeCount: result.likeCount }));
      if (result.liked) setBursting(true);
    } catch (error) {
      toast(error instanceof Error ? error.message : "点赞失败", "danger");
    } finally {
      setBusy(false);
    }
  };

  const toggleFollow = async () => {
    if (!user) {
      requestLogin("请先登录账号，才可以关注用户。", window.location.pathname);
      return;
    }
    if (user.id === post.author.id) return;
    const following = Boolean(post.author.isFollowing);
    setFollowBusy(true);
    try {
      const result = await api<{ following: boolean; followerCount: number }>(`/users/${post.author.id}/follow`, { method: following ? "DELETE" : "PUT" });
      setPost((current) => ({ ...current, author: { ...current.author, isFollowing: result.following } }));
      window.dispatchEvent(new CustomEvent("share-moments:follow-updated", { detail: { authorId: post.author.id, following: result.following } }));
      toast(result.following ? "已关注，后续动态会在互动提醒中出现" : "已取消关注", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "关注操作失败", "danger");
    } finally {
      setFollowBusy(false);
    }
  };

  const transferPost = async () => {
    setTransferBusy(true);
    try {
      await api<{ post: Post }>(`/posts/${post.id}/transfer`, { method: "POST" });
      setTransferComplete(true);
      setTransferDialogOpen(false);
      toast("已转存到你的私有内容仓库", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "动态转存失败", "danger");
    } finally {
      setTransferBusy(false);
    }
  };

  const updateGalleryOffset = (value: number) => {
    galleryOffsetRef.current = value;
    stageRef.current?.style.setProperty("--gallery-swipe-offset", `${value}px`);
  };

  const commitGalleryStep = (action: Exclude<GalleryTransitionAction, "reset">) => {
    updateZoom(1);
    setGalleryIndex((current) => {
      if (current == null) return 0;
      return action === "previous" ? current - 1 : current + 1;
    });
  };

  const finishGalleryTransition = () => {
    const action = galleryTransitionRef.current;
    if (!action) return;
    galleryTransitionRef.current = null;
    if (galleryTransitionTimerRef.current != null) {
      window.clearTimeout(galleryTransitionTimerRef.current);
      galleryTransitionTimerRef.current = null;
    }
    stageRef.current?.classList.remove("is-settling");
    if (action !== "reset") flushSync(() => commitGalleryStep(action));
    updateGalleryOffset(0);
  };

  const settleGallery = (action: GalleryTransitionAction) => {
    if (!post.media.length || galleryTransitionRef.current) return;
    if (action !== "reset" && post.media.length < 2) return;
    if (action === "reset" && Math.abs(galleryOffsetRef.current) < .5) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      updateGalleryOffset(0);
      if (action !== "reset") commitGalleryStep(action);
      return;
    }
    const stage = stageRef.current;
    if (!stage) {
      if (action !== "reset") commitGalleryStep(action);
      return;
    }
    galleryTransitionRef.current = action;
    stage.classList.add("is-settling");
    const target = action === "next" ? -stage.clientWidth : action === "previous" ? stage.clientWidth : 0;
    window.requestAnimationFrame(() => updateGalleryOffset(target));
    galleryTransitionTimerRef.current = window.setTimeout(finishGalleryTransition, 320);
  };

  const showPrevious = () => settleGallery("previous");
  const showNext = () => settleGallery("next");

  const openGallery = (index: number) => {
    galleryTransitionRef.current = null;
    stageRef.current?.classList.remove("is-settling");
    updateGalleryOffset(0);
    updateZoom(1);
    setGalleryIndex(index);
  };

  const pointerDistance = () => {
    const points = [...pointersRef.current.values()];
    if (points.length < 2) return 0;
    return Math.hypot(points[0]!.x - points[1]!.x, points[0]!.y - points[1]!.y);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest("button")) return;
    if (galleryTransitionRef.current) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = { x: event.clientX, y: event.clientY };
    pointersRef.current.set(event.pointerId, point);
    if (pointersRef.current.size === 1) {
      const imageBounds = imageRef.current?.getBoundingClientRect();
      swipeRef.current = {
        ...point,
        startedOnImage: Boolean(imageBounds && point.x >= imageBounds.left && point.x <= imageBounds.right && point.y >= imageBounds.top && point.y <= imageBounds.bottom)
      };
      if (zoomRef.current > 1) dragRef.current = { pointerId: event.pointerId, start: point, origin: panRef.current };
    }
    if (pointersRef.current.size === 2) {
      multiTouchRef.current = true;
      pinchRef.current = { distance: pointerDistance(), scale: zoomRef.current };
    }
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointersRef.current.size === 2 && pinchRef.current) {
      event.preventDefault();
      updateZoom(pinchRef.current.scale * pointerDistance() / Math.max(pinchRef.current.distance, 1));
    } else if (pointersRef.current.size === 1 && dragRef.current?.pointerId === event.pointerId && zoomRef.current > 1) {
      event.preventDefault();
      updatePan(clampPan({
        x: dragRef.current.origin.x + event.clientX - dragRef.current.start.x,
        y: dragRef.current.origin.y + event.clientY - dragRef.current.start.y
      }));
    } else if (pointersRef.current.size === 1 && swipeRef.current && zoomRef.current === 1 && post.media.length > 1) {
      const deltaX = event.clientX - swipeRef.current.x;
      const deltaY = event.clientY - swipeRef.current.y;
      if (Math.abs(deltaX) > Math.abs(deltaY) * .7) {
        event.preventDefault();
        const width = stageRef.current?.clientWidth ?? window.innerWidth;
        updateGalleryOffset(Math.max(-width, Math.min(width, deltaX)));
      }
    }
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = swipeRef.current;
    let closePreview = false;
    if (pointersRef.current.size === 1 && start) {
      const deltaX = event.clientX - start.x;
      const deltaY = event.clientY - start.y;
      const action = resolveGalleryGesture({
        deltaX,
        deltaY,
        zoomScale: zoomRef.current,
        wasMultiTouch: multiTouchRef.current,
        allowTapClose: start.startedOnImage
      });
      if (action === "previous") showPrevious();
      if (action === "next") showNext();
      if (action === "close") closePreview = true;
      if (!action) settleGallery("reset");
    }
    pointersRef.current.delete(event.pointerId);
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 1 && zoomRef.current > 1) {
      const [pointerId, point] = [...pointersRef.current.entries()][0]!;
      dragRef.current = { pointerId, start: point, origin: panRef.current };
    }
    if (pointersRef.current.size === 0) {
      swipeRef.current = null;
      multiTouchRef.current = false;
    }
    if (closePreview) setGalleryIndex(null);
  };

  const handlePointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(event.pointerId);
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 0) {
      swipeRef.current = null;
      multiTouchRef.current = false;
      settleGallery("reset");
    }
  };

  const handleGalleryTransitionEnd = (event: ReactTransitionEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && event.propertyName === "transform") finishGalleryTransition();
  };

  useEffect(() => {
    if (galleryIndex == null || post.media.length < 2) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        showPrevious();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        showNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [galleryIndex, post.media.length]);

  return (
    <Card component="article" variant="outlined" className={`post-card${linksToDetail ? " post-card-clickable" : ""}${compact ? " post-card-compact" : ""}${detail ? " post-card-detail" : ""}${post.listingState === "sold" ? " post-card-sold" : ""}`}>
      {linksToDetail ? <Link className="post-card-detail-link" to={`/posts/${post.id}`} aria-label={`查看${post.author.nickname}发布的${contentLabel}详情`}><span className="sr-only">查看动态详情</span></Link> : null}
      <header className="post-header">
        {isolated ? <div className="author-button">
          <Avatar className="avatar" src={post.author.avatarUrl ?? undefined}>{initials(post.author.nickname)}</Avatar>
          <span className="author-copy"><span><strong>{post.author.nickname}</strong>{post.author.role === "seller" ? <Chip className="seller-badge" color="primary" variant="outlined" size="small" label="号商" /> : null}</span></span>
        </div> : <ButtonBase className="author-button" type="button" aria-label={`查看${post.author.nickname}的主页`} onClick={() => navigate(`/seller/${post.author.id}`)}>
          <Avatar className="avatar" src={post.author.avatarUrl ?? undefined}>{initials(post.author.nickname)}</Avatar>
          <span className="author-copy"><span><strong>{post.author.nickname}</strong>{post.author.role === "seller" ? <Chip className="seller-badge" color="primary" variant="outlined" size="small" label="号商" /> : null}</span></span>
        </ButtonBase>}
        <div className="post-header-side">
          {showFollow && !isolated && user?.id !== post.author.id ? <Button className={`post-follow-button${post.author.isFollowing ? " is-following" : ""}`} variant={post.author.isFollowing ? "outline" : "primary"} size="sm" type="button" disabled={followBusy} aria-pressed={Boolean(post.author.isFollowing)} aria-label={post.author.isFollowing ? `取消关注${post.author.nickname}` : `关注${post.author.nickname}`} startIcon={followBusy ? <LoaderCircle className="spin" aria-hidden="true" /> : post.author.isFollowing ? <BellRing aria-hidden="true" /> : <BellPlus aria-hidden="true" />} onClick={() => void toggleFollow()}>{post.author.isFollowing ? "已关注" : "关注"}</Button> : null}
          <div className="post-header-meta">
            <time dateTime={post.publishedAt ?? post.createdAt}>{formatRelativeTime(post.publishedAt ?? post.createdAt)}</time>
            {post.isPinned ? <span className="post-pinned-badge"><Pin aria-hidden="true" />置顶</span> : null}
            {post.profilePinned ? <span className="post-pinned-badge"><Pin aria-hidden="true" />主页置顶</span> : null}
            {post.visibility === "self" ? <span className="status-badge">私有动态</span> : null}
            {post.listingState === "sold" ? <span className="status-badge status-sold">已售出</span> : null}
            {post.status !== "published" ? <span className={`status-badge status-${post.status}`}>{post.status === "pending" ? "审核中" : post.status === "rejected" ? "未通过" : "已下架"}</span> : null}
          </div>
        </div>
      </header>
      <div className={`post-content${post.media.length ? "" : " post-content-no-media"}`}>
        <div className="post-copy">
          {post.transferSource ? <Link className="post-transfer-source" to={post.transferSource.postId ? `/posts/${post.transferSource.postId}` : `/seller/${post.transferSource.author.id}`}><FolderInput aria-hidden="true" /><span>转存自 <strong>{post.transferSource.author.nickname}</strong></span><span>查看来源</span></Link> : null}
          {hasTags || post.displayNote ? <div className={`post-info-row${hasTags ? " has-tags" : ""}${post.displayNote ? " has-display-note" : ""}`}>
            {hasTags ? <div className="tag-row">{post.gameName ? <Chip className="tag tag-game" color="primary" size="small" label={post.gameName} /> : null}{post.serverName ? <Chip className="tag" variant="outlined" size="small" label={post.serverName} /> : null}{post.category ? <Chip className="tag post-category" size="small" label={post.category.name} /> : null}</div> : <span aria-hidden="true" />}
            {post.displayNote ? <div className="post-display-note-box" aria-label={`动态备注：${post.displayNote}`}><strong>{post.displayNote}</strong></div> : null}
          </div> : null}
          {detail ? <p className="post-intro">{post.intro}</p> : <ExpandableText key={post.id} className="post-intro" text={post.intro} lines={4} />}
          {post.highlights.length ? <ul className="feature-list" aria-label="动态要点">{post.highlights.map((item) => <li key={item}><CircleCheck aria-hidden="true" />{item}</li>)}</ul> : null}
          {post.reviewReason && post.status !== "published" ? <p className="review-reason">审核说明：{post.reviewReason}</p> : null}
        </div>
        {post.media.length ? (
          <div className={`media-grid media-count-${Math.min(post.media.length, 4)}`}>
            {post.media.slice(0, 4).map((media, index) => (
              <ButtonBase key={media.id} type="button" onClick={() => openGallery(index)} aria-label={`查看第 ${index + 1} 张动态图片`}>
                <img src={media.url} alt={`${contentLabel}图片 ${index + 1}`} />
                {index === 3 && post.media.length > 4 ? <span className="more-media">+{post.media.length - 4}</span> : null}
              </ButtonBase>
            ))}
            {post.media.length > 1 ? <span className="media-count"><Images aria-hidden="true" />{post.media.length} 张</span> : null}
          </div>
        ) : null}
      </div>
      <footer className="post-actions">
        <div className="post-action-group">
          {isolated ? <><span className="post-view-count" aria-label={`${post.likeCount} 个赞`}><Heart aria-hidden="true" />{post.likeCount}</span><span className="post-view-count" aria-label={`${post.viewCount} 次浏览`}><Eye aria-hidden="true" />{post.viewCount}</span></> : <>
            <Button className={`post-action t-like${post.likedByMe ? " is-active" : ""}${bursting ? " is-bursting" : ""}`} data-liked={post.likedByMe} variant="ghost" size="sm" type="button" aria-pressed={post.likedByMe} disabled={busy} onClick={() => void toggleLike()}><span className="t-like-icon"><Heart className="t-like-heart" aria-hidden="true" /></span><span className="t-like-particles" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /><i /></span><span>点赞</span><span className="post-like-count">{post.likeCount}</span></Button>
            <span className="post-view-count" aria-label={`${post.viewCount} 次浏览`}><Eye aria-hidden="true" />{post.viewCount}</span>
          </>}
        </div>
        {isolated ? <span className="post-comment-action" aria-label={`${post.commentCount} 条评论`}><MessagesSquare aria-hidden="true" />{post.commentCount}</span> : detail ? <span className="post-comment-action" aria-label={`${post.commentCount} 条评论`}><MessagesSquare aria-hidden="true" />{post.commentCount}</span> : <Link className="post-comment-action" to={`/posts/${post.id}#comment-composer`} aria-label={`查看 ${post.commentCount} 条评论并发表评论`}><MessagesSquare aria-hidden="true" />{post.commentCount}</Link>}
        {!isolated && (ownerActions || contentAction || canTransfer || transferComplete) ? <div className={`post-content-actions${ownerActions ? " post-owner-actions" : ""}`}>
          {ownerActions}
          {contentAction}
          {canTransfer || transferComplete ? <Button variant="outline" size="sm" type="button" disabled={transferComplete} onClick={() => setTransferDialogOpen(true)}><FolderInput aria-hidden="true" />{transferComplete ? "已转存" : "转存"}</Button> : null}
          {productHref ? <Button className={ownerActions ? "post-mall-action" : undefined} asChild variant="outline" size="sm"><a href={productHref} target="_blank" rel="noopener noreferrer"><Store aria-hidden="true" />{config?.mallEntryLabel || "前往商城"}<ExternalLink aria-hidden="true" /></a></Button> : null}
        </div> : !isolated && productHref ? <Button asChild variant="outline" size="sm"><a href={productHref} target="_blank" rel="noopener noreferrer"><Store aria-hidden="true" />{config?.mallEntryLabel || "前往商城"}<ExternalLink aria-hidden="true" /></a></Button> : null}
      </footer>

      <Dialog className="post-transfer-dialog" open={transferDialogOpen} onOpenChange={(open) => { if (!transferBusy) setTransferDialogOpen(open); }}>
        <DialogContent className="post-transfer-dialog-content">
          <DialogClose asChild><Button className="dialog-close" variant="ghost" size="icon" aria-label="关闭转存确认"><X aria-hidden="true" /></Button></DialogClose>
          <span className="post-transfer-dialog-icon"><FolderInput aria-hidden="true" /></span>
          <DialogTitle>转存到内容仓库</DialogTitle>
          <DialogDescription>将 {post.author.nickname} 的这条公开动态保存为你的私有仓库副本。</DialogDescription>
          <div className="post-transfer-dialog-boundary"><strong>转存后会发生什么</strong><span>副本保留原作者来源，不复制商品链接，也不会立即公开。你可以在内容仓库中查看和管理。</span></div>
          <div className="dialog-actions"><DialogClose asChild><Button variant="outline" type="button" disabled={transferBusy}>取消</Button></DialogClose><Button type="button" disabled={transferBusy} onClick={() => void transferPost()}>{transferBusy ? <LoaderCircle className="spin" aria-hidden="true" /> : <FolderInput aria-hidden="true" />}{transferBusy ? "正在转存" : "确认转存"}</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog className="gallery-modal" open={galleryOpen} onOpenChange={(open) => { if (!open) setGalleryIndex(null); }}>
        <DialogContent className="gallery-dialog">
          <DialogTitle className="sr-only">{contentLabel}图片预览</DialogTitle>
          <DialogDescription className="sr-only">手机端可左右滑动浏览，点击当前图片关闭预览；桌面端可使用切换按钮，放大后可以拖动查看图片细节</DialogDescription>
          <div className="gallery-shell">
            <header className="gallery-toolbar">
              <div className="gallery-heading"><Images aria-hidden="true" /><span><strong>{contentLabel}</strong><small>第 {activeGalleryIndex == null ? 0 : activeGalleryIndex + 1} 张，共 {post.media.length} 张</small></span></div>
              <div className="gallery-toolbar-actions">
                <Tooltip title="缩小"><span><IconButton type="button" aria-label="缩小图片" disabled={zoomScale <= 1} onClick={() => updateZoom(zoomScale - .5)}><ZoomOut aria-hidden="true" /></IconButton></span></Tooltip>
                <span className="gallery-zoom-label" aria-live="polite">{Math.round(zoomScale * 100)}%</span>
                <Tooltip title="放大"><IconButton type="button" aria-label="放大图片" disabled={zoomScale >= 4} onClick={() => updateZoom(zoomScale + .5)}><ZoomIn aria-hidden="true" /></IconButton></Tooltip>
                <Tooltip title="恢复原始大小"><span><IconButton type="button" aria-label="恢复图片大小" disabled={zoomScale === 1} onClick={() => updateZoom(1)}><RotateCcw aria-hidden="true" /></IconButton></span></Tooltip>
                <DialogClose asChild><IconButton className="gallery-close" type="button" aria-label="关闭图片预览"><X aria-hidden="true" /></IconButton></DialogClose>
              </div>
            </header>
            <div ref={stageRef} className={`gallery-stage${zoomScale > 1 ? " is-zoomed" : ""}${post.media.length > 1 ? " is-switchable" : ""}`} role="region" aria-label="图片预览区域，左右滑动切换图片，点击当前图片关闭预览，放大后可拖动查看" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerCancel}>
              {gallerySlides.map((slide) => {
                const media = post.media[slide.index]!;
                const active = slide.position === "active";
                return <div className={`gallery-slide gallery-slide-${slide.position}`} key={slide.key} aria-hidden={active ? undefined : "true"} onTransitionEnd={active ? handleGalleryTransitionEnd : undefined}>
                  <img ref={active ? imageRef : undefined} className={active ? "gallery-image" : "gallery-adjacent-image"} draggable={false} loading="eager" decoding="async" style={active ? { transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoomScale})` } : undefined} src={media.url} alt={active ? `${contentLabel}大图 ${(activeGalleryIndex ?? 0) + 1}` : ""} />
                </div>;
              })}
              {post.media.length > 1 ? <><IconButton className="gallery-previous" type="button" aria-label="查看上一张" onClick={showPrevious}><ArrowLeft aria-hidden="true" /></IconButton><IconButton className="gallery-next" type="button" aria-label="查看下一张" onClick={showNext}><ArrowRight aria-hidden="true" /></IconButton></> : null}
            </div>
            <footer className="gallery-footer">
              {post.media.length > 1 ? <div className="gallery-thumbnails" aria-label="选择预览图片">{post.media.map((media, index) => <ButtonBase className={`gallery-thumbnail${activeGalleryIndex === index ? " is-active" : ""}`} key={media.id} type="button" aria-label={`查看第 ${index + 1} 张图片`} aria-current={activeGalleryIndex === index ? "true" : undefined} onClick={() => openGallery(index)}><img src={media.url} alt="" /><span>{index + 1}</span></ButtonBase>)}</div> : <span className="gallery-single-label">{zoomScale > 1 ? "拖动查看图片细节" : "双指缩放图片"}</span>}
              <span className="gallery-count">{activeGalleryIndex == null ? 0 : activeGalleryIndex + 1} / {post.media.length}</span>
            </footer>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
